import _ from "lodash";
import React, { forwardRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { recordEvent } from "lib/eventTracker";
import { updateChildId } from "Administrator/modules/AdministratorModule";
import { setActiveHomepageCard } from "Teacher/modules/TeacherModule";
import { connect } from "react-redux";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import ACLStore from "lib/aclStore";
import moment from "moment";
import { useEffectEvent, useI18n, useMount } from "Hooks";
import { goToRelativeRoute } from "modules/Services";
import { PUBLISH_PERM_VALUES } from "AnnouncementsManager/routes/ReviewerSetup/modules/utils";
import { parseConfigurationValue } from "Utils";
import {
  getActiveTab,
  getIsStaff,
  getSelectedAcademicYear,
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
  getUserId,
  getOrganizationId,
  getOrganizationCurriculumPrograms,
  getUserCurriculumPrograms,
  getChildId,
  getUserType,
  getSelectedCurriculumProgram,
} from "modules/CommonSelectors";
import {
  ADMIN,
  STAFF,
  STUDENT,
  STUDENT_USER,
  STAFF_USER,
  FAMILY_MEMBER,
  PARENT,
} from "Constants/stringConstants";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import {
  getDefaultOrderBy as getDefaultOrderByStudent,
  getFormattedStudentName,
} from "Utils/GetFormattedStudentName";
import { colors } from "Constants";
import ProfileCascade from "UIComponents/ProfileCascade";
import ACLStorage from "lib/aclService";
import {
  ANNOUNCEMENT_CREATE_PUBLISH_PERMISSION,
  ANNOUNCEMENT_CREATE_PUBLISH_CLASS_PERMISSION,
  ANNOUNCEMENT_VIEW_ALL_PERMISSION,
  ANNOUNCEMENT_CREATE_PUBLISH_PROGRAM_PERMISSION,
  ANNOUNCEMENT_MODULE_PERMISSION,
  CLASS_ANNOUNCEMENT_MANAGE_PERMISSION,
  CLASS_ANNOUNCEMENT_MODULE_PERMISSION,
  ANNOUNCEMENT_BIN_FEATURE_FLAG,
  ANNOUNCEMENT_LIVE_SYNC_FEATURE_FLAG,
  ANNOUNCEMENT_V3_FEATURE_FLAG,
  DISABLE_SEND_EMAIL_FEATURE_FLAG,
  ANNOUNCEMENT_AI_ASSISTANT,
  ANNOUNCEMENT_24H_FORMAT_FEATURE_FLAG,
  ANNOUNCEMENT_DEFAULT_BANNER_FEATURE_FLAG,
  ANNOUNCEMENT_FEATURE_FLAG,
  ANNOUNCEMENT_REVIEW_FEATURE_FLAG,
  CLASS_ANNOUNCEMENT_FEATURE_FLAG,
  DUAL_PANEL_MANAGER_V2_FEATURE_FLAG,
  BIG_ORG_OPTIMIZATION_FEATURE_FLAG,
} from "Announcements/modules/constants";
import { ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION } from "Constants/permissionConstants";
import {
  getUserCoursesV2FromCache,
  getDefaultBannerFromCache,
  getUserLearningCourseClassesFromCache,
  getOrganizationGradesFromCache,
} from "./AnnouncementGraphqlHelpers";
import { ATTACHMENT_OMIT_ATTRIBUTES } from "store/static";
import {
  getStudentAdditionalFieldsData,
  STUDENT_ADDITIONAL_FIELDS_TITLE_MAPPING,
} from "StudentPortfolio/routes/PortfolioDetailsV2/modules/Utils";
import { getIsAccountConfigurationEnabled } from "Utils/ACLStoreUtils";
import { getIsCombineCurriculumHomePageEnabled } from "Platform/modules/PlatformUtils";
import { HOME_PAGE_FEATURE_ANNOUNCEMENT } from "Teacher/routes/Courses/components/TeacherCourses/Constants";
import { getHomepageFeatureVisibilityStatus } from "Platform/routes/HomePage/modules/HomePageUtils";
import { getIsAIEnabledInAnnouncementModule } from "./aclStoreUtils";

let store;
import("store/createStore").then(importedModule => {
  store = importedModule.store;
});

export const isAnnouncementsV2 = () => ACLStore.can(ANNOUNCEMENT_FEATURE_FLAG);
export const getIsAnnouncementYearGroupEnabled = () =>
  ACLStore.can("FeatureFlag:AnnouncementYearGroupEnabled");

export const isAnnouncementGlobalHeaderRevampEnabled = () =>
  ACLStore.can("FeatureFlag:AnnouncementGlobalHeaderRevampEnabled");

export const getDefaultSendEmailInAnnouncement = () =>
  ACLStore.can("FeatureFlag:SendMailInAnnouncement");

export const getIsAdminTab = state => getActiveTab(state) === ADMIN;
export const getIsReviewFlowEnabled = ({
  isInClassAnnouncement = false,
} = {}) =>
  ACLStore.can(ANNOUNCEMENT_REVIEW_FEATURE_FLAG) && !isInClassAnnouncement;
export const getIsAnnouncementHeaderV2Enabled = () =>
  ACLStore.can("FeatureFlag:ShowAnnouncementPreviewV2");

export const getHasCreateOnlyAccess = ({
  isInClassAnnouncement = false,
} = {}) =>
  parseConfigurationValue(
    ACLStore.getConfigObj("StaffPermission:CircularPublishPermission")
  ) === PUBLISH_PERM_VALUES.CREATE_ONLY && !isInClassAnnouncement;
export const getIsAnnouncementBinEnabled = () =>
  ACLStore.can(ANNOUNCEMENT_BIN_FEATURE_FLAG);
export const getIsLiveSyncEnabled = () =>
  ACLStore.can(ANNOUNCEMENT_LIVE_SYNC_FEATURE_FLAG);

export const getIsAnnouncementsV3Enabled = () =>
  ACLStore.can(ANNOUNCEMENT_V3_FEATURE_FLAG);

export const getHasStudentAddFilterEnabled = () => {
  return ACLStore.can("FeatureFlag:AddStudentAddInfoFilterInAnnouncement");
};

export const getIsSendEmailDisabled = () =>
  ACLStore.can(DISABLE_SEND_EMAIL_FEATURE_FLAG);

export const getIsClassAnnouncementEnabled = () =>
  ACLStore.can(CLASS_ANNOUNCEMENT_FEATURE_FLAG);

export const getIsAnnouncementAIAssistantEnabled = () =>
  ACLStore.can(ANNOUNCEMENT_AI_ASSISTANT);

export const getIs24HourFormatEnabled = () =>
  ACLStore.can(ANNOUNCEMENT_24H_FORMAT_FEATURE_FLAG);

export const getIsDefaultBannerEnabled = () =>
  ACLStore.can(ANNOUNCEMENT_DEFAULT_BANNER_FEATURE_FLAG);

export const isDualPanelManagerV2Enabled = () =>
  ACLStore.can(DUAL_PANEL_MANAGER_V2_FEATURE_FLAG);

export const getIsBigOrgOptimizationEnabled = () =>
  ACLStore.can(BIG_ORG_OPTIMIZATION_FEATURE_FLAG);

export const getAIAnnouncementConfig = () => {
  const isAiEnabledInAnnouncement = getIsAIEnabledInAnnouncementModule();
  const isAIAssistantEnabled =
    isAiEnabledInAnnouncement && getIsAnnouncementAIAssistantEnabled();

  return {
    showAIInAnnouncementRTE: isAiEnabledInAnnouncement && !isAIAssistantEnabled,
    isAIAssistantEnabled,
  };
};

// function created to avoid adding shallow-equal(double equals)
// code everywhere I need to compare Id's
export const isIdEqual = (id1, id2) => id1 == id2;

export const userTypePermMap = {
  staff: "Common:Announcement",
  student: "StudentApp:Announcement",
  parent: "FamilyApp:Announcement",
};

export const ANNOUNCEMENT_TABS = {
  INBOX: "INBOX",
  DRAFTS: "DRAFTS",
  SCHEDULED: "SCHEDULED",
  SENT: "SENT",
  UNDER_REVIEW: "UNDER_REVIEW",
  TO_REVIEW: "TO_REVIEW",
  ALL_PUBLISHED: "ALL_PUBLISHED",
  BIN: "BIN",
  IN_CLASS_ALL: "IN_CLASS_ALL",
};

export const ANNOUNCEMENT_PREVIEW_TABS = {
  WEB: "WEB",
  TABLET: "TABLET",
  MOBILE: "MOBILE",
};

export const ANNOUNCEMENT_PREVIEW_TYPES = {
  TODDLE: "TODDLE",
  EMAIL: "EMAIL",
};

export const ANNOUNCEMENT_V2_FILTERS = {
  SCHOOLS: "SCHOOLS",
  CREATED_BY: "CREATED_BY",
  CLASSES: "CLASSES",
  PUBLISH_DATE: "PUBLISH_DATE",
  RECIPIENT_TYPE: "RECIPIENT_TYPE",
  STATUS_TYPE: "STATUS_TYPE",
};

export const CREATED_IN_OPTIONS = {
  GLOBAL: "GLOBAL",
  CLASS: "CLASS",
};

export const ANNOUNCEMENT_V2_FILTER_INITIAL_STATE = {
  SCHOOLS: [],
  PUBLISH_DATE: {
    condition: "PUBLISH_BETWEEN",
    date: null,
  },
  CREATED_BY: [],
  CLASSES: [],
  RECIPIENT_TYPE: [],
  IS_READ: "ALL",
  STATUS_TYPE: [], // this is to handle is read state with global header revamp enabled
};

export const getAnnouncementV2FilterInClassInitialState = selectedCourseId => ({
  ...ANNOUNCEMENT_V2_FILTER_INITIAL_STATE,
  CLASSES: [selectedCourseId],
});

export const TABS_WITH_HEADER_FILTERS = [
  ANNOUNCEMENT_TABS.ALL_PUBLISHED,
  ANNOUNCEMENT_TABS.INBOX,
];

export const ANNOUNCEMENT_STATUSES = {
  ALL: "ALL",
  DRAFT: "DRAFT",
  SCHEDULED: "SCHEDULED",
  PUBLISHED: "PUBLISHED",
  REVIEW_PENDING: "REVIEW_PENDING",
  REVIEW_REJECTED: "REVIEW_REJECTED",
  ARCHIVED: "ARCHIVED",
};

export const FEED_TAB_VALUES = {
  createdByMe: "ME",
  createdByOthers: "OTHERS",
  createdByAll: "ALL",
};

export const SORT_ORDER = {
  ASC: "ASC",
  DESC: "DESC",
};

export const SORT_BY = {
  UPDATED_AT: "UPDATED_AT",
  PUBLISHED_AT: "PUBLISHED_AT",
  ARCHIVED_AT: "ARCHIVED_AT",
};
export const GET_ANNOUNCEMENT_FEED_COUNT = 20;

export const CIRCULAR_UPDATE_KEY_STATUSES = {
  PUBLISHED: "PUBLISHED",
  UNPUBLISHED: "UNPUBLISHED",
  ARCHIVED: "ARCHIVED",
};

export const CIRCULAR_UPDATE_KEY_PATHS = {
  CREATE: "CREATE",
  UPDATE: "UPDATE",
};

// map "created by me" tabs to the announcement statuses
const mapStatusToTab = {
  [ANNOUNCEMENT_STATUSES.DRAFT]: ANNOUNCEMENT_TABS.DRAFTS,
  [ANNOUNCEMENT_STATUSES.SCHEDULED]: ANNOUNCEMENT_TABS.SCHEDULED,
  [ANNOUNCEMENT_STATUSES.PUBLISHED]: ANNOUNCEMENT_TABS.SENT,
  // creator's both pending and rejected reviewed announcements will be shown in under review tab
  [ANNOUNCEMENT_STATUSES.REVIEW_PENDING]: ANNOUNCEMENT_TABS.UNDER_REVIEW,
  [ANNOUNCEMENT_STATUSES.REVIEW_REJECTED]: ANNOUNCEMENT_TABS.UNDER_REVIEW,
  [ANNOUNCEMENT_STATUSES.ARCHIVED]: ANNOUNCEMENT_TABS.BIN,
};

export const DESCRIPTION = "description";

const allStates = _.filter(
  _.values(ANNOUNCEMENT_STATUSES),
  it => it !== ANNOUNCEMENT_STATUSES.ALL
);

const mapFiltersToTab = {
  [ANNOUNCEMENT_TABS.ALL_PUBLISHED]: {
    createdBy: FEED_TAB_VALUES.createdByAll,
    status: ANNOUNCEMENT_STATUSES.PUBLISHED,
  },
  [ANNOUNCEMENT_TABS.INBOX]: {
    createdBy: FEED_TAB_VALUES.createdByOthers,
    status: ANNOUNCEMENT_STATUSES.PUBLISHED,
  },
  [ANNOUNCEMENT_TABS.DRAFTS]: {
    createdBy: FEED_TAB_VALUES.createdByMe,
    status: ANNOUNCEMENT_STATUSES.DRAFT,
  },
  [ANNOUNCEMENT_TABS.SCHEDULED]: {
    createdBy: FEED_TAB_VALUES.createdByMe,
    status: ANNOUNCEMENT_STATUSES.SCHEDULED,
  },
  [ANNOUNCEMENT_TABS.SENT]: {
    createdBy: FEED_TAB_VALUES.createdByMe,
    status: ANNOUNCEMENT_STATUSES.PUBLISHED,
  },
  [ANNOUNCEMENT_TABS.UNDER_REVIEW]: {
    createdBy: FEED_TAB_VALUES.createdByMe,
    status: [
      ANNOUNCEMENT_STATUSES.REVIEW_PENDING,
      ANNOUNCEMENT_STATUSES.REVIEW_REJECTED,
    ],
  },
  [ANNOUNCEMENT_TABS.TO_REVIEW]: {
    createdBy: FEED_TAB_VALUES.createdByOthers,
    status: ANNOUNCEMENT_STATUSES.REVIEW_PENDING,
  },
  [ANNOUNCEMENT_TABS.BIN]: {
    status: ANNOUNCEMENT_STATUSES.ARCHIVED,
  },
};

export const mapTabsToTitle = {
  [ANNOUNCEMENT_TABS.INBOX]: "common:inbox",
  [ANNOUNCEMENT_TABS.DRAFTS]: "common:drafts",
  [ANNOUNCEMENT_TABS.SCHEDULED]: "common:scheduled",
  [ANNOUNCEMENT_TABS.SENT]: "common:sent",
  [ANNOUNCEMENT_TABS.UNDER_REVIEW]: "announcement:under_review",
  [ANNOUNCEMENT_TABS.TO_REVIEW]: "announcement:to_review",
  [ANNOUNCEMENT_TABS.BIN]: "common:trash",
};

const ERROR_CODES = {
  PERM_CHANGED: "1001",
  ALREADY_REVIEWED: "1002",
  ALREADY_ARCHIVED: "1003",
  STALE_UPDATE: "1005",
};

const ERROR_MESSAGES = {
  [ERROR_CODES.PERM_CHANGED]: hasCreateOnlyAccess =>
    hasCreateOnlyAccess
      ? "toastMsgs:create_only_to_create_and_publish_perm_change_toast_msg"
      : "toastMsgs:create_and_publish_to_create_only_perm_change_toast_msg",
  [ERROR_CODES.ALREADY_REVIEWED]: "toastMsgs:announcement_already_reviewed",
  [ERROR_CODES.ALREADY_ARCHIVED]: "toastMsgs:announcement_already_archived",
  [ERROR_CODES.STALE_UPDATE]: "toastMsgs:announcement_stale_update",
  default: "toastMsgs:something_went_wrong",
};

export const ANNOUNCEMENT_MODAL = {
  BACK_WARNING: "BACK_WARNING",
  NOTIFICATION: "NOTIFICATION",
  SCHEDULE: "SCHEDULE",
};

export const ANNOUNCEMENT_FORM_STATUS = {
  IDLE: "IDLE",
  SAVING: "SAVING",
  SAVED: "SAVED",
};

export const getAnnouncementInitialState = ({ selectedCourseId = "" } = {}) => {
  const isAnnouncementsV3Enabled = getIsAnnouncementsV3Enabled();
  const sendEmail = getDefaultSendEmailInAnnouncement();
  const isInClassAnnouncement = !_.isEmpty(selectedCourseId);
  return {
    title: null,
    description: null,
    bannerV2: null,
    aiConversation: null,
    ...(!isAnnouncementsV3Enabled
      ? {
          audioMessage: undefined,
          sendEmail,
        }
      : {}),
    attachments: [],
    entityType:
      isAnnouncementsV3Enabled || isInClassAnnouncement
        ? ENTITY_TYPE.COURSE
        : undefined,
    sharedWith: [],
    emailSharedWith: [],
    publishedAt: undefined, // only for schedule.
    isPublished: false,
    programmes: [],
    courses: isInClassAnnouncement ? [selectedCourseId] : [],
    grades: [],
    users: [],
    state: ANNOUNCEMENT_STATUSES.DRAFT,
    totalRecipients: 0,
    totalCourses: 0,
  };
};

export const getErrorToastMessage = errorCode => {
  if (errorCode === ERROR_CODES.PERM_CHANGED) {
    const hasCreateOnlyAccess = getHasCreateOnlyAccess();
    return ERROR_MESSAGES[ERROR_CODES.PERM_CHANGED](hasCreateOnlyAccess);
  }
  return ERROR_MESSAGES[errorCode] || "toastMsgs:something_went_wrong";
};

export const getIsRnpConsumptionAllowed = () => {
  return ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION);
};

// only use this when state cannot be read, or when the variable cannot be passed
export const getSelectedCourseId = () => {
  const state = store.getState();
  const selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course",
    ""
  );

  return selectedCourseId;
};

export const getCurrentCurriculumProgram = () => {
  const state = store.getState();
  return getSelectedCurriculumProgram(state);
};

export const getControlParameterContext = ({
  selectedCourseId: selectedCourseIdArg,
} = {}) => {
  // no need to check RnP in CCH orgs as BE is responsible for computing the visibility of modules
  if (getIsCombineCurriculumHomePageEnabled()) {
    const isModuleVisible =
      getHomepageFeatureVisibilityStatus(HOME_PAGE_FEATURE_ANNOUNCEMENT) ===
      "VISIBLE";
    return {
      isAccessCheckSkipped: isModuleVisible,
      isAccessBlocked: !isModuleVisible,
    };
  }

  const state = store.getState();
  // route param wins over Redux so direct/refresh of in-class create/update
  // resolves to CLASS_ANNOUNCEMENTS before teacher.selected_class hydrates
  const selectedCourseId = selectedCourseIdArg ?? getSelectedCourseId();

  const activeTab = _.get(state, "login.activeTab", "");
  const isFromAdminPage = activeTab === "admin";

  const userCurriculumPrograms = getUserCurriculumPrograms();
  const adminPortalContext = _.map(userCurriculumPrograms, curriculum => ({
    entityId: _.get(curriculum, "id"),
    entityType: "CURRICULUM_PROGRAM",
  }));
  const controlParametersContext = isFromAdminPage
    ? [adminPortalContext]
    : undefined;

  let controlParameter = ANNOUNCEMENT_MODULE_PERMISSION;

  if (!_.isEmpty(selectedCourseId)) {
    controlParameter = CLASS_ANNOUNCEMENT_MODULE_PERMISSION;
  } else if (isFromAdminPage) {
    // for admin portal we need to check canPublishToAll permission
    controlParameter = ANNOUNCEMENT_CREATE_PUBLISH_PERMISSION;
  }

  const controlParameterObj = {
    type: isFromAdminPage ? "PERMISSION" : "MODULE_SETTINGS",
    controlParameter,
  };

  return {
    controlParametersContext,
    controlParameterObj,
  };
};

export const getUserProgramsWithAnnouncementModuleEnabled = () => {
  const state = store.getState();
  const userPrograms = getUserCurriculumPrograms(state);

  const programWisePermission = _.map(userPrograms, curriculum => {
    const { id, acronym, label } = curriculum;
    const isModuleEnabledForProgram = ACLStorage.getModuleSettingsValueByContext(
      ANNOUNCEMENT_MODULE_PERMISSION,
      [{ entityId: id, entityType: "CURRICULUM_PROGRAM" }]
    );
    return {
      id,
      label,
      acronym,
      permission: isModuleEnabledForProgram,
    };
  });

  return _.map(
    _.filter(programWisePermission, item => item.permission),
    ({ id, acronym, label }) => ({ id, acronym, label })
  );
};

export const getOrganizationProgramsWithAnnouncementModuleEnabled = () => {
  const state = store.getState();
  const organizationCurriculumPrograms = getOrganizationCurriculumPrograms(
    state
  );

  const programWisePermission = _.map(
    organizationCurriculumPrograms,
    curriculum => {
      const { id, acronym, label } = curriculum;
      const isModuleEnabledForProgram = ACLStorage.getModuleSettingsValueByContext(
        ANNOUNCEMENT_MODULE_PERMISSION,
        [{ entityId: id, entityType: "CURRICULUM_PROGRAM" }]
      );
      return {
        id,
        label,
        acronym,
        permission: isModuleEnabledForProgram,
      };
    }
  );

  return _.map(
    _.filter(programWisePermission, item => item.permission),
    ({ id, acronym, label }) => ({ id, acronym, label })
  );
};

export const getUserProgramsWithPublishPermission = () => {
  const userCurriculumPrograms = getUserCurriculumPrograms();

  const programWisePermission = _.map(userCurriculumPrograms, curriculum => {
    const { id, acronym, label } = curriculum;
    const isModuleEnabledForProgram = ACLStorage.getModuleSettingsValueByContext(
      ANNOUNCEMENT_MODULE_PERMISSION,
      [{ entityId: id, entityType: "CURRICULUM_PROGRAM" }]
    );
    const hasCreateAndPublishPermission = ACLStorage.getPermissionValueByContext(
      ANNOUNCEMENT_CREATE_PUBLISH_PROGRAM_PERMISSION,
      [{ entityId: id, entityType: "CURRICULUM_PROGRAM" }]
    );
    return {
      id,
      label,
      acronym,
      permission: isModuleEnabledForProgram && hasCreateAndPublishPermission,
    };
  });

  return _.map(
    _.filter(programWisePermission, item => item.permission),
    ({ id, acronym, label }) => ({ id, acronym, label })
  );
};

export const getCanPublishAnnouncementToSelectedProgram = () => {
  const state = store.getState();
  const currentCurriculumProgramId = getSelectedCurriculumId(state);

  const activeTab = _.get(state, "login.activeTab", "");
  const isFromAdminPage = activeTab === "admin";
  if (isFromAdminPage) {
    return getCanPublishAnnouncementToAll();
  }
  const userProgramsWithPublishPermission = getUserProgramsWithPublishPermission();
  return _.includes(
    _.map(userProgramsWithPublishPermission, "id"),
    currentCurriculumProgramId
  );
};

export const getCanPublishAnnouncementToAll = () => {
  return ACLStorage.getPermissionValue(ANNOUNCEMENT_CREATE_PUBLISH_PERMISSION);
};

export const getCanPublishAnnouncementToProgram = () => {
  const isRnpConsumptionAllowed = getIsRnpConsumptionAllowed();
  if (!isRnpConsumptionAllowed) {
    return ACLStorage.getPermissionValue(
      ANNOUNCEMENT_CREATE_PUBLISH_PROGRAM_PERMISSION
    );
  }
  const programsWithPublishPerms = getUserProgramsWithPublishPermission();

  return !_.isEmpty(programsWithPublishPerms);
};

export const getCanPublishAnnouncementToClass = () => {
  const isRnpConsumptionAllowed = getIsRnpConsumptionAllowed();
  if (!isRnpConsumptionAllowed) {
    return ACLStorage.getPermissionValue(
      ANNOUNCEMENT_CREATE_PUBLISH_CLASS_PERMISSION
    );
  }
  const state = store.getState();
  const academicYearId = getSelectedAcademicYearId(state);

  const curriculumPrograms = getAccessibleCurriculumPrograms();
  const curriculumProgramIds = _.map(curriculumPrograms, "id");

  const variables = {
    filters: {
      fetchOnlyMappedCourses: false,
      controlParameters: [
        ANNOUNCEMENT_CREATE_PUBLISH_CLASS_PERMISSION,
        ANNOUNCEMENT_CREATE_PUBLISH_PROGRAM_PERMISSION,
        ANNOUNCEMENT_CREATE_PUBLISH_PERMISSION,
      ],
      curriculumProgramIds,
      academicYears: [academicYearId],
    },
    id: getUserId(),
    getGrades: true,
    getCurriculumProgram: false,
    userType: "STAFF",
  };
  const node = getUserCoursesV2FromCache(variables);
  return !!_.get(node, "coursesV2.totalCount", 0);
};

export const getAccessibleCurriculumPrograms = () => {
  const canPublishToAll = getCanPublishAnnouncementToAll();

  return canPublishToAll
    ? getOrganizationProgramsWithAnnouncementModuleEnabled()
    : getUserProgramsWithAnnouncementModuleEnabled();
};

export const getCurriculumProgramIds = (getOnlyIds = true) => {
  const state = store.getState();
  if (getCanPublishAnnouncementToAll()) {
    const programsWithModuleEnabled = getOrganizationProgramsWithAnnouncementModuleEnabled();
    return getOnlyIds
      ? _.map(programsWithModuleEnabled, "id")
      : programsWithModuleEnabled;
  }

  if (!getIsRnpConsumptionAllowed()) {
    const { id, label, acronym } = getSelectedCurriculumProgram(state);
    return getOnlyIds ? [id] : [{ id, label, acronym }];
  }

  if (getCanPublishAnnouncementToProgram()) {
    const userProgramsWithPublishPermission = getUserProgramsWithPublishPermission();
    return getOnlyIds
      ? _.map(userProgramsWithPublishPermission, "id")
      : userProgramsWithPublishPermission;
  }

  return [];
};

export const getCanManageClassAnnouncement = () => {
  const isRnpEnabled = getIsRnpConsumptionAllowed();
  const userType = getUserType();

  if (!isRnpEnabled) {
    return userType === STAFF; // fallback: allow in case if RnP is not enabled and user is staff
  }

  return ACLStorage.getPermissionValue(CLASS_ANNOUNCEMENT_MANAGE_PERMISSION);
};

export const getCanCreateAnnouncement = () => {
  const selectedCourseId = getSelectedCourseId();

  if (!_.isEmpty(selectedCourseId)) {
    return getCanManageClassAnnouncement();
  }

  return (
    getCanPublishAnnouncementToAll() ||
    getCanPublishAnnouncementToProgram() ||
    getCanPublishAnnouncementToClass()
  );
};

export const getIsAllPublishedTabVisible = () => {
  const state = store.getState();
  const activeTab = _.get(state, "login.activeTab", "");
  const isFromAdminPage = activeTab === "admin";
  const isCCHEnabled = getIsCombineCurriculumHomePageEnabled();
  if (isFromAdminPage || isCCHEnabled) {
    const curriculumPrograms = getAccessibleCurriculumPrograms();
    const allPublishedTabVisibleForProgram = _.map(
      curriculumPrograms,
      ({ id }) => ({
        isVisible: ACLStorage.getPermissionValueByContext(
          ANNOUNCEMENT_VIEW_ALL_PERMISSION,
          [{ entityId: id, entityType: "CURRICULUM_PROGRAM" }]
        ),
      })
    );
    return _.some(allPublishedTabVisibleForProgram, { isVisible: true });
  }
  return ACLStorage.getPermissionValue(ANNOUNCEMENT_VIEW_ALL_PERMISSION);
};

export const getHasAdminAccessForAnnouncement = () => {
  const selectedCourseId = getSelectedCourseId();
  if (!_.isEmpty(selectedCourseId)) {
    return getCanManageClassAnnouncement();
  }

  const isRnpConsumptionAllowed = getIsRnpConsumptionAllowed();
  const canPublishToAll = getCanPublishAnnouncementToAll();
  const canPublishToProgram = getCanPublishAnnouncementToProgram();
  const canPublishToSelectedProgram = getCanPublishAnnouncementToSelectedProgram();

  return (
    canPublishToAll ||
    (isRnpConsumptionAllowed
      ? canPublishToSelectedProgram
      : canPublishToProgram)
  );
};

const getAnnouncementFilterInterval = ({ filterObj = {}, dateRange }) => {
  const { condition, date } = filterObj;

  if (_.isEmpty(date)) {
    return dateRange;
  }

  switch (condition) {
    case "PUBLISH_BETWEEN": {
      return {
        startDate: date?.startDate,
        endDate: date.endDate ? date.endDate : moment().format("YYYY-MM-DD"),
      };
    }
    case "PUBLISH_BEFORE": {
      return {
        endDate: date,
        startDate: dateRange.startDate,
      };
    }
    case "PUBLISH_AFTER": {
      return {
        startDate: date,
        endDate: dateRange.endDate,
      };
    }
    default:
      return {
        startDate: date,
        endDate: date,
      };
  }
};

const reviewStates = [
  ANNOUNCEMENT_STATUSES.REVIEW_PENDING,
  ANNOUNCEMENT_STATUSES.REVIEW_REJECTED,
];

export const getAnnouncementFilters = ({
  isInClassAnnouncement = false,
  filters: {
    searchText,
    filtersV2 = {},
    createdByIds = [],
    dateRange = {},
    tab = ANNOUNCEMENT_TABS.INBOX,
    // following param is for the cases where we directly want to pass
    // createdBy and status filter without using tab parameter(eg: Search)
    lowLevelFilters, // { createdBy: String, status: String }
  } = {},
  canPublishToAll = getCanPublishAnnouncementToAll(),
  currentCurriculumProgramId,
  academicYearId,
  studentIds,
}) => {
  const {
    PUBLISH_DATE = {},
    CREATED_BY = [],
    CLASSES = [],
    RECIPIENT_TYPE = [],
    IS_READ = "ALL",
    STATUS_TYPE = [],
    SCHOOLS = [],
  } = filtersV2;

  const { createdBy, status } = _.isEmpty(lowLevelFilters)
    ? mapFiltersToTab[tab]
    : lowLevelFilters;

  const { endDate, startDate } = getAnnouncementFilterInterval({
    filterObj: PUBLISH_DATE,
    dateRange,
  });

  const isReviewState = _.isArray(status)
    ? _.intersection(status, reviewStates).length > 0
    : _.includes(reviewStates, status);
  // TODO: check for all status case after intro of new statuses
  const filterObj = {
    createdBy,
    states: (!status || status === ANNOUNCEMENT_STATUSES.ALL
      ? allStates
      : _.isArray(status)
      ? status
      : [status]
    ).filter(
      state =>
        getIsAnnouncementBinEnabled() ||
        state !== ANNOUNCEMENT_STATUSES.ARCHIVED
    ),

    searchText,
    orderBy:
      isInClassAnnouncement &&
      _.includes(STATUS_TYPE, ANNOUNCEMENT_STATUSES.DRAFT)
        ? SORT_BY.UPDATED_AT
        : status === ANNOUNCEMENT_STATUSES.ARCHIVED
        ? SORT_BY.ARCHIVED_AT
        : isReviewState || status === ANNOUNCEMENT_STATUSES.DRAFT
        ? SORT_BY.UPDATED_AT
        : SORT_BY.PUBLISHED_AT,
    orderByDirection:
      !_.isArray(status) && status === ANNOUNCEMENT_STATUSES.SCHEDULED
        ? SORT_ORDER.ASC
        : SORT_ORDER.DESC,
    academicYearId,
    studentIds,
    createdIn: isInClassAnnouncement
      ? CREATED_IN_OPTIONS.CLASS
      : CREATED_IN_OPTIONS.GLOBAL,
  };

  const filterCreatedByIds = !_.isEmpty(createdByIds)
    ? createdByIds
    : CREATED_BY;
  if (
    _.includes(TABS_WITH_HEADER_FILTERS, tab) &&
    !_.isEmpty(filterCreatedByIds)
  ) {
    filterObj.createdByIds = filterCreatedByIds;
  }

  if (!_.isEmpty(RECIPIENT_TYPE)) {
    filterObj.sharedWith = RECIPIENT_TYPE;
  }

  if (!_.isEqual(IS_READ, "ALL")) {
    filterObj.isRead = _.isEqual(IS_READ, "READ") ? true : false;
  }

  if (isAnnouncementGlobalHeaderRevampEnabled()) {
    // if any one status filter is selected and status is not DRAFT
    if (
      _.eq(_.size(STATUS_TYPE), 1) &&
      !_.includes(STATUS_TYPE, ANNOUNCEMENT_STATUSES.DRAFT)
    ) {
      filterObj.isRead = _.includes(STATUS_TYPE, "READ");
    }
  }

  if (!_.isEmpty(CLASSES)) {
    filterObj.courseIds = CLASSES;
  }

  if (getIsCombineCurriculumHomePageEnabled()) {
    filterObj.curriculumProgramIds = _.isEmpty(SCHOOLS) ? undefined : SCHOOLS;
  } else if (!canPublishToAll && currentCurriculumProgramId) {
    // Show all announcements to admin irrespective of selected curriculum
    // To teacher only show the announcements of selected curriculum
    filterObj.curriculumProgramIds = [currentCurriculumProgramId];
  }
  if (endDate && startDate) {
    filterObj.createdAfter = moment(startDate).startOf("day").toISOString();
    filterObj.createdBefore = moment(endDate).endOf("day").toISOString();
  }
  return filterObj;
};

export const getBadgeNo = ({
  filters,
  optionKey,
  isInClassAnnouncement = false,
}) => {
  const propertyValue = _.get(filters, optionKey);
  switch (optionKey) {
    case ANNOUNCEMENT_V2_FILTERS.PUBLISH_DATE: {
      const { condition } = propertyValue;
      const date =
        condition === "PUBLISH_BETWEEN"
          ? _.get(propertyValue.date, "startDate", null)
          : propertyValue?.date;

      return date && moment(date).isValid() ? 1 : 0;
    }
    case ANNOUNCEMENT_V2_FILTERS.CLASSES: {
      if (isInClassAnnouncement) {
        return 0;
      }
      return _.size(propertyValue);
    }
    default:
      return _.size(propertyValue);
  }
};

export const getPublisherOptions = ({
  publishers,
  profileCascadeProps = {},
}) => {
  return _.map(publishers, member => {
    const {
      id,
      isDivider,
      profileImage,
      // optional props
      avatarId,
      filterButtonLabel,
      getDropDownLabel = fullName => fullName,
    } = member;

    if (isDivider) return { key: id, ...member };

    const fullName = getFormattedUserName({
      user: { node: member },
    });
    const avatarItem = {
      id: avatarId || id,
      value: fullName,
      imageUrl: profileImage,
    };

    return {
      key: id,
      label: getDropDownLabel(fullName),
      filterButtonLabel,
      icon: (
        <ProfileCascade
          items={[avatarItem]}
          showAvatar
          {...profileCascadeProps}
        />
      ),
    };
  });
};

export const getSelectedCreator = ({
  createdBy,
  createdByIds,
  publisherOptions,
}) => {
  let createdByKey = createdBy;
  if (
    createdByKey !== FEED_TAB_VALUES.createdByMe &&
    _.isArray(createdByIds) &&
    !_.isEmpty(createdByIds)
  ) {
    [createdByKey] = createdByIds;
  }
  return _.find(publisherOptions, publisher =>
    isIdEqual(publisher.key, createdByKey)
  );
};

export const getStaffCircularConfigVariables = ({
  currentCurriculumProgramId,
  academicYearId,
  academicYearSelected,
}) => {
  const { startDate, endDate } = academicYearSelected;

  return {
    id: getUserId(),
    input: {
      academicYearId,
      curriculumProgramId: getCanPublishAnnouncementToAll()
        ? undefined
        : currentCurriculumProgramId,
      createdAfter: moment(startDate).startOf("day").format(),
      createdBefore: moment(endDate).endOf("day").format(),
    },
  };
};

export const getReadDropDownOptions = ({ t }) => {
  return [
    {
      key: "ALL",
      label: t("common:all"),
    },
    {
      key: "READ",
      label: t("common:read"),
    },
    {
      key: "UNREAD",
      label: t("classRoom:unread"),
    },
  ];
};

export const getTabBasedOnFilters = ({
  announcement,
  userId,
  isInClassAnnouncement,
}) => {
  const state = store.getState();
  const { createdBy, state: status, isSharedWithMe } = announcement;
  const createdById = _.get(createdBy, "id");
  const isStaff = getIsStaff(state);
  const isCreatedByCurrentUser = isIdEqual(userId, createdById);

  if (isCreatedByCurrentUser) {
    if (isInClassAnnouncement && status === ANNOUNCEMENT_STATUSES.PUBLISHED) {
      return ANNOUNCEMENT_TABS.ALL_PUBLISHED;
    }
    return mapStatusToTab[status];
  }
  // if review is pending for announcement not created by me, meaning assigned
  // to me as a reviewer, so show it in TO_REVIEW tab
  if (status === ANNOUNCEMENT_STATUSES.REVIEW_PENDING) {
    return ANNOUNCEMENT_TABS.TO_REVIEW;
  }
  if (status === ANNOUNCEMENT_STATUSES.ARCHIVED) {
    return ANNOUNCEMENT_TABS.BIN;
  }
  if (isInClassAnnouncement && isStaff) {
    return ANNOUNCEMENT_TABS.ALL_PUBLISHED;
  }

  // announcement is scheduled by the reviewer
  if (
    getIsReviewFlowEnabled({ isInClassAnnouncement }) &&
    status === ANNOUNCEMENT_STATUSES.SCHEDULED &&
    !isCreatedByCurrentUser
  ) {
    return ANNOUNCEMENT_TABS.TO_REVIEW;
  }

  if (!getIsAllPublishedTabVisible()) return ANNOUNCEMENT_TABS.INBOX;

  return isSharedWithMe || !isStaff
    ? ANNOUNCEMENT_TABS.INBOX
    : ANNOUNCEMENT_TABS.ALL_PUBLISHED;
};

export const ACTION_BUTTONS = {
  DRAFT: "DRAFT",
  PUB_AND_SCHEDULE: "PUB_AND_SCHEDULE",
  UPDATE: "UPDATE",
  SEND_FOR_REVIEW: "SEND_FOR_REVIEW",
  RETURN_FROM_REVIEW: "RETURN_FROM_REVIEW",
  PUBLISH: "PUBLISH",
  SCHEDULE: "SCHEDULE",
};

export const getActionButtonsVisibility = ({
  createdById,
  userId,
  announcementId,
  isInClassAnnouncement = false,
  state,
}) => {
  const isReviewerFlowEnabled = getIsReviewFlowEnabled({
    isInClassAnnouncement,
  });
  const isAnnouncementId = !_.isUndefined(announcementId);
  const isCreatedByLoggedInUser = isIdEqual(createdById, userId);
  const isDeleted = state === ANNOUNCEMENT_STATUSES.ARCHIVED;

  // when restoring the deleted announcement
  if (isDeleted) {
    if (getHasCreateOnlyAccess({ isInClassAnnouncement })) {
      return {
        [ACTION_BUTTONS.DRAFT]: true,
        [ACTION_BUTTONS.SEND_FOR_REVIEW]: true,
      };
    }
    return {
      [ACTION_BUTTONS.DRAFT]: true,
      [ACTION_BUTTONS.PUB_AND_SCHEDULE]: true,
    };
  }

  if (isReviewerFlowEnabled) {
    // if user is editing or reviewing someone else's created announcement
    if (isAnnouncementId && !isCreatedByLoggedInUser) {
      // Based on state, return buttons
      // PUBLISHED: superior role editing someone else's published announcement
      // REVIEW_PENDING: reviewer is reviewing assigned announcement
      const buttonsPerState = {
        [ANNOUNCEMENT_STATUSES.PUBLISHED]: { [ACTION_BUTTONS.UPDATE]: true },
        [ANNOUNCEMENT_STATUSES.REVIEW_PENDING]: {
          [ACTION_BUTTONS.RETURN_FROM_REVIEW]: true,
          [ACTION_BUTTONS.PUB_AND_SCHEDULE]: true,
        },
      };

      return _.get(buttonsPerState, state, {});
    }

    // Now the only case left is logged in user creating or editing own announcement
    // Return buttons based on access level
    return getHasCreateOnlyAccess({ isInClassAnnouncement })
      ? {
          [ACTION_BUTTONS.DRAFT]: true,
          [ACTION_BUTTONS.SEND_FOR_REVIEW]: true,
        }
      : {
          [ACTION_BUTTONS.DRAFT]: true,
          [ACTION_BUTTONS.PUB_AND_SCHEDULE]: true,
        };
  }

  // editing announcement published by others, accessed through superior access
  // i.e curriculum admin or system admins
  if (isAnnouncementId && !isCreatedByLoggedInUser) {
    return { [ACTION_BUTTONS.UPDATE]: true };
  }

  // Creating or editing our own announcement
  return {
    [ACTION_BUTTONS.DRAFT]: true,
    [ACTION_BUTTONS.PUB_AND_SCHEDULE]: true,
  };
};

export const getSubText = ({
  t,
  user,
  defaultStudentNameOrderPreference = getDefaultOrderByStudent(),
}) => {
  const { type: userType } = user;
  switch (userType) {
    case STUDENT: {
      const grade = _.get(user, "yearGroup.grade.name");
      return grade
        ? t("common:student_hyphen_label", {
            label: grade,
          })
        : t("common:student");
    }

    case STAFF:
      return user.displayRole;

    case PARENT: {
      const childrenCount = _.size(_.get(user, "children", []));
      if (childrenCount === 1) {
        return t("common:family_member_with_label", {
          label: getFormattedStudentName({
            student: _.first(user.children),
            orderBy: defaultStudentNameOrderPreference,
          }),
        });
      }

      if (childrenCount > 1) {
        return t("common:family_member_with_label_and_count", {
          label: getFormattedStudentName({
            student: _.first(user.children),
            orderBy: defaultStudentNameOrderPreference,
          }),
          count: childrenCount,
        });
      }

      return t("common:family_of_count_student_other", {
        count: 0,
      });
    }
    default:
      return null;
  }
};

// NOTE: order of array matters, it should be biggest to smallest

export const getIsScheduledForLater = ({ updatedAt, publishedAt }) => {
  // if publishedAt is null, means announcement is not scheduled/ published yet
  if (_.isNil(publishedAt)) return false;
  const updated = moment(updatedAt);
  const published = moment(publishedAt);
  return published > updated;
};

export const getIsArchivedAfterSent = ({ archivedAt, publishedAt }) => {
  const archived = moment(archivedAt);
  const published = moment(publishedAt);
  return archived > published;
};

export const AUDIO_ATTACHMENT_TYPES = {
  AUDIO: "AUDIO",
  CIRCULAR_AUDIO_DESCRIPTION: "CIRCULAR_AUDIO_DESCRIPTION",
};

export const CIRCULAR_ATTACHMENT_TYPES = {
  DEFAULT_CIRCULAR_BANNER: "DEFAULT_CIRCULAR_BANNER",
  CIRCULAR_BANNER: "CIRCULAR_BANNER",
  CIRCULAR: "CIRCULAR",
  ...AUDIO_ATTACHMENT_TYPES,
};

export const getIsAcademicYearEnded = academicYearSelected => {
  const { endDate } = academicYearSelected;
  return moment().isAfter(moment(endDate), "day");
};

export const getIsAnnouncementInSelectedAcademicYear = ({
  academicYearSelected,
  announcementCreatedAt,
}) => {
  const { startDate, endDate } = academicYearSelected;
  return moment(announcementCreatedAt).isBetween(
    moment(startDate),
    moment(endDate)
  );
};

export const getAllAttachments = ({ audioMessage, attachments }) => {
  const newAudioAttachment = audioMessage
    ? _.omit(audioMessage, "__typename")
    : undefined;

  return _.isEmpty(newAudioAttachment)
    ? attachments
    : [newAudioAttachment, ...attachments];
};

// custom hook to sort attachment list, to put recorded audios at beginning
export const useSortedAttachmentList = attachments => {
  const sortedAttachmentList = useMemo(
    () =>
      _.sortBy(attachments, ({ metadata, type }) => {
        const metadataType = _.get(metadata, "type");

        return type === AUDIO_ATTACHMENT_TYPES.AUDIO &&
          metadataType === AUDIO_ATTACHMENT_TYPES.CIRCULAR_AUDIO_DESCRIPTION
          ? 0
          : 1;
      }),
    [attachments]
  );

  return sortedAttachmentList;
};

// common padding I need to use at different places in recipients modal
export const recipientModalPaddingStyles = {
  paddingLeft: "32px",
  paddingRight: "32px",
  width: "100%",
};

// common styles for preview panels
export const getPreviewPanelContainerStyle = () => ({
  borderRadius: "12px",
  backgroundColor: colors.white,
  ...(isAnnouncementGlobalHeaderRevampEnabled()
    ? { border: `1px solid #E6E6E6` }
    : {}),
});

export const getIsOnSearchRoute = ({ location }) =>
  location.pathname.includes("/search");

export const withAnnouncementRouteUpdates = WrappedComponent => {
  const WithAnnouncementRouteUpdates = forwardRef(
    function WithAnnouncementRouteUpdates(props, ref) {
      const { t } = useI18n(["announcement"]);

      const {
        setActiveHomepageCard,
        updateChildId,
        isAdminTab,
        ...restProps
      } = props;

      useMount(() => {
        if (isAdminTab) updateChildId("CIRCULARS");
        else {
          setActiveHomepageCard({
            from: "home-page",
            label: t("announcement:announcements"),
          });
        }
      });

      return <WrappedComponent ref={ref} {...restProps} />;
    }
  );

  const mapStateToProps = state => ({
    isAdminTab: getIsAdminTab(state),
  });

  WithAnnouncementRouteUpdates.propTypes = {
    isAdminTab: PropTypes.bool.isRequired,
    setActiveHomepageCard: PropTypes.func.isRequired,
    updateChildId: PropTypes.func.isRequired,
  };

  return connect(mapStateToProps, {
    setActiveHomepageCard,
    updateChildId,
  })(WithAnnouncementRouteUpdates);
};

/**
 * This is temporarily needed, its purpose is to show top loader bar as
 * withLoader does on refetch. This would be used where I am not using
 * withLoader, but need the top-bar loader. Will stop its usage once
 * I start using withLoader, after it start to support custom central loader
 */
export const withTopBarLoader = WrappedComponent => {
  const WithTopBarLoader = forwardRef(function WithTopBarLoader(props, ref) {
    const {
      isData,
      isLoading,
      showLoading,
      hideLoading,
      showLoaderBar,
    } = props;

    useEffect(() => {
      if (isData && showLoaderBar && isLoading) showLoading();
      else hideLoading();
    }, [isLoading, isData, showLoaderBar, showLoading, hideLoading]);

    useEffect(
      () => () => {
        // hide loader on unmount irrespective of conditions
        hideLoading();
      },
      [hideLoading]
    );

    return <WrappedComponent ref={ref} {...props} />;
  });

  WithTopBarLoader.propTypes = {
    isData: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    showLoaderBar: PropTypes.bool,
    showLoading: PropTypes.func.isRequired,
    hideLoading: PropTypes.func.isRequired,
  };

  WithTopBarLoader.defaultProps = {
    showLoaderBar: true,
  };

  return connect(undefined, { showLoading, hideLoading })(WithTopBarLoader);
};

/**
 * This will render correct component based on Announcements feature flag
 * and will also help in redirecting to parent circulars route, for the routes
 * which aren't supported in V1
 * @param {{ V1Component: ReactComponent, V2Component: ReactComponent | undefined }} param0
 * @returns { WrappedReactComponent }
 */
export const withV2Announcements = ({
  V1Component,
  V2Component,
  v1FallbackRoute = "../",
}) => {
  const WithV2Announcements = ({ goToRelativeRoute, ...restProps }) => {
    const isV2 = useMemo(isAnnouncementsV2, []);

    const navigateToParentRoute = useEffectEvent(() => {
      goToRelativeRoute({ route: v1FallbackRoute, type: "replace" });
    });

    useEffect(() => {
      // if there isn't any supported component for current route in V1,
      // redirect to parent route
      if (!isV2 && !V1Component) navigateToParentRoute();
    }, [isV2, navigateToParentRoute]);

    if (isV2) return <V2Component {...restProps} />;
    return V1Component ? <V1Component {...restProps} /> : null;
  };

  WithV2Announcements.propTypes = {
    goToRelativeRoute: PropTypes.func.isRequired,
  };

  return connect(undefined, { goToRelativeRoute })(WithV2Announcements);
};

export const TAB_STATE = {
  COMPOSE: "COMPOSE",
  POST_SETTINGS: "POST_SETTINGS",
};

export const DROPDOWN_OPTIONS = {
  PROGRAMS: "PROGRAMS",
  GRADES: "GRADES",
};

export const ENTITY_TYPE = {
  CURRICULUM_PROGRAM: "CURRICULUM_PROGRAM",
  GRADE: "GRADE",
  COURSE: "COURSE",
  USER: "USER",
};

export const COURSE_COUNT_BY_ENTITY_FILTER_INPUT = {
  CURRICULUM_PROGRAM_IDS: "curriculumProgramIds",
  GRADES: "grades",
};

export const withCreatePermission = ({ route = "../" } = {}) => {
  return WrappedComponent => {
    const WithCreatePermission = props => {
      const { isCreationAllowed, goToRelativeRoute } = props;

      const navigateToParentRoute = useEffectEvent(() => {
        goToRelativeRoute({ route, type: "replace" });
      });

      useEffect(() => {
        if (!isCreationAllowed) navigateToParentRoute();
      }, [navigateToParentRoute, isCreationAllowed]);

      if (!isCreationAllowed) return null;
      return <WrappedComponent {...props} />;
    };

    WithCreatePermission.propTypes = {
      isCreationAllowed: PropTypes.bool.isRequired,
      goToRelativeRoute: PropTypes.func.isRequired,
    };

    const mapStateToProps = state => {
      const academicYearSelected = getSelectedAcademicYear(state);
      const isRNPAnnouncementCreationAllowed = getCanCreateAnnouncement();
      const isStaff = getIsStaff(state);
      const isCurrentAcademicYearEnded = getIsAcademicYearEnded(
        academicYearSelected
      );

      return {
        isCreationAllowed:
          isRNPAnnouncementCreationAllowed &&
          isStaff &&
          !isCurrentAcademicYearEnded,
      };
    };

    const mapDispatchToProps = { goToRelativeRoute };
    return connect(mapStateToProps, mapDispatchToProps)(WithCreatePermission);
  };
};

export const MOMENT_FORMAT = {
  DISPLAY_DATE_TIME_FORMAT: "DD MMM YYYY, h:mm a",
  DISPLAY_DATE_TIME_FORMAT_24H: "DD MMM YYYY, HH:mm",
  DATE_PICKER_FORMAT: "DD-MM-YYYY",
  DISPLAY_DATE_TIME_FORMAT_FOOTER: "DD MMM YYYY, h:mmA",
  DISPLAY_DATE_TIME_FORMAT_FOOTER_24H: "DD MMM YYYY, HH:mm",
  DATE_FORMAT_PREV_RECIPIENTS: "Do MMMM YYYY",
  EMAIL_PREVIEW_DATE_FORMAT: "D MMM, YYYY [at] h:mm A",
  EMAIL_PREVIEW_DATE_FORMAT_24H: "D MMM, YYYY [at] HH:mm",
};

export const getTimeFormat = formatKey => {
  const is24HourEnabled = getIs24HourFormatEnabled();
  const format24HKey = `${formatKey}_24H`;

  if (is24HourEnabled && MOMENT_FORMAT[format24HKey]) {
    return MOMENT_FORMAT[format24HKey];
  }

  return MOMENT_FORMAT[formatKey];
};

export const getEntityTypeOptions = ({
  t = () => {},
  isInClassAnnouncement = false,
  testId,
}) => {
  const canPublishToProgram = getCanPublishAnnouncementToProgram();
  const canPublishToAll = getCanPublishAnnouncementToAll();
  const options =
    (canPublishToProgram || canPublishToAll) && !isInClassAnnouncement
      ? [
          {
            key: ENTITY_TYPE.CURRICULUM_PROGRAM,
            label: getIsAccountConfigurationEnabled()
              ? t("common:school_plural")
              : t("common:programmes"),
            value: ENTITY_TYPE.CURRICULUM_PROGRAM,
            testId: testId ? `${testId}-entityTypeList-programme` : undefined,
          },
          {
            key: ENTITY_TYPE.GRADE,
            label: t("common:grade_plural"),
            value: ENTITY_TYPE.GRADE,
            testId: testId ? `${testId}-entityTypeList-grade` : undefined,
          },
        ]
      : [];

  options.push(
    ...[
      {
        key: ENTITY_TYPE.COURSE,
        label: t("common:class_plural"),
        value: ENTITY_TYPE.COURSE,
        testId: testId ? `${testId}-entityTypeList-course` : undefined,
      },
      {
        key: ENTITY_TYPE.USER,
        label: t("common:custom"),
        value: ENTITY_TYPE.USER,
        testId: testId ? `${testId}-entityTypeList-custom` : undefined,
      },
    ]
  );
  return options;
};

export const SHARE_WITH_RECIPIENT_TYPE = {
  STAFF: STAFF_USER,
  STUDENT: STUDENT_USER,
  FAMILY_MEMBER,
};

export const mapSharedWithToUserType = {
  [SHARE_WITH_RECIPIENT_TYPE.STUDENT]: STUDENT,
  [SHARE_WITH_RECIPIENT_TYPE.STAFF]: STAFF,
  [SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER]: PARENT,
};

export const mapUserTypeToSharedWith = {
  [STUDENT]: SHARE_WITH_RECIPIENT_TYPE.STUDENT,
  [STAFF]: SHARE_WITH_RECIPIENT_TYPE.STAFF,
  [PARENT]: SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER,
};

export const mapSharedWithToMetadata = {
  [SHARE_WITH_RECIPIENT_TYPE.STAFF]: {
    label: "common:staff_member_other",
    countLabel: "common:staff",
    countKey: "staffCount",
    countKeyEmail: "staffHasEmailCount",
  },
  [SHARE_WITH_RECIPIENT_TYPE.STUDENT]: {
    label: "common:student_plural",
    countLabel: "common:student_plural_lowercase",
    countKey: "studentCount",
    countKeyEmail: "studentHasEmailCount",
  },
  [SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER]: {
    label: "common:family_member_plural",
    countLabel: "common:family_member_plural",
    countKey: "familyCount",
    countKeyEmail: "familyHasEmailCount",
  },
};

export const getRecipientOptions = (t, testId) => {
  return [
    {
      key: SHARE_WITH_RECIPIENT_TYPE.STAFF,
      label: t("common:staff_members_plural"),
      value: SHARE_WITH_RECIPIENT_TYPE.STAFF,
      testId: `${testId}-staff`,
    },
    {
      key: SHARE_WITH_RECIPIENT_TYPE.STUDENT,
      label: t("common:student_plural"),
      value: SHARE_WITH_RECIPIENT_TYPE.STUDENT,
      testId: `${testId}-student`,
    },
    {
      key: SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER,
      label: t("common:family_member_plural"),
      value: SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER,
      testId: `${testId}-family`,
    },
  ];
};

export const getPreviewTabs = (t, testId) => {
  return [
    {
      key: ANNOUNCEMENT_PREVIEW_TABS.WEB,
      label: t("common:web"),
      value: ANNOUNCEMENT_PREVIEW_TABS.WEB,
      testId: `${testId}-web`,
    },
    {
      key: ANNOUNCEMENT_PREVIEW_TABS.TABLET,
      label: t("common:tablet"),
      value: ANNOUNCEMENT_PREVIEW_TABS.TABLET,
      testId: `${testId}-tablet`,
    },
    {
      key: ANNOUNCEMENT_PREVIEW_TABS.MOBILE,
      label: t("common:mobile"),
      value: ANNOUNCEMENT_PREVIEW_TABS.MOBILE,
      testId: `${testId}-mobile`,
    },
  ];
};

export const mapEntityTypeToKey = {
  [ENTITY_TYPE.CURRICULUM_PROGRAM]: "programmes",
  [ENTITY_TYPE.COURSE]: "courses",
  [ENTITY_TYPE.GRADE]: "grades",
  [ENTITY_TYPE.USER]: "users",
};

export const mapEntityTypeToLabel = {
  get [ENTITY_TYPE.CURRICULUM_PROGRAM]() {
    return getIsAccountConfigurationEnabled()
      ? "common:school_plural"
      : "common:programmes";
  },
  [ENTITY_TYPE.GRADE]: "common:grade_plural",
  [ENTITY_TYPE.COURSE]: "common:class_plural",
  [ENTITY_TYPE.USER]: "common:custom",
};

export const getUserCountByEntity = ({
  entityType,
  users = [],
  selectedUserGroups,
  selectedEmailUserGroups = [],
  userCountByEntity = {},
  includeEmailCount = true,
}) => {
  if (entityType !== ENTITY_TYPE.USER) {
    const {
      staffCount,
      staffHasEmailCount,
      studentCount,
      studentHasEmailCount,
      familyCount,
      familyHasEmailCount,
    } = userCountByEntity;

    const totalCount =
      (_.includes(selectedUserGroups, SHARE_WITH_RECIPIENT_TYPE.STAFF)
        ? staffCount
        : 0) +
      (_.includes(selectedUserGroups, SHARE_WITH_RECIPIENT_TYPE.STUDENT)
        ? studentCount
        : 0) +
      (_.includes(selectedUserGroups, SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER)
        ? familyCount
        : 0);

    const totalHasEmailCount =
      (_.includes(selectedUserGroups, SHARE_WITH_RECIPIENT_TYPE.STAFF)
        ? staffHasEmailCount
        : 0) +
      (_.includes(selectedUserGroups, SHARE_WITH_RECIPIENT_TYPE.STUDENT)
        ? studentHasEmailCount
        : 0) +
      (_.includes(selectedUserGroups, SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER)
        ? familyHasEmailCount
        : 0);

    const totalHasEmailCountSelected =
      (_.includes(selectedEmailUserGroups, SHARE_WITH_RECIPIENT_TYPE.STAFF)
        ? staffHasEmailCount
        : 0) +
      (_.includes(selectedEmailUserGroups, SHARE_WITH_RECIPIENT_TYPE.STUDENT)
        ? studentHasEmailCount
        : 0) +
      (_.includes(
        selectedEmailUserGroups,
        SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER
      )
        ? familyHasEmailCount
        : 0);

    return {
      staffCount,
      studentCount,
      familyCount,
      totalCount,
      ...(includeEmailCount
        ? {
            staffHasEmailCount,
            studentHasEmailCount,
            familyHasEmailCount,
            totalHasEmailCount,
            totalHasEmailCountSelected,
          }
        : {}),
    };
  }

  const initialCounts = {
    [STAFF]: { total: 0, emailCount: 0 },
    [STUDENT]: { total: 0, emailCount: 0 },
    [PARENT]: { total: 0, emailCount: 0 },
  };

  const counts = _.reduce(
    users,
    (acc, user) => {
      const { type, email } = user;
      if (!acc[type]) {
        return acc;
      }
      acc[type].total += 1;
      if (_.trim(email) && includeEmailCount) {
        acc[type].emailCount += 1;
      }
      return acc;
    },
    initialCounts
  );

  const { total: staffCount, emailCount: staffHasEmailCount } = counts[STAFF];
  const { total: studentCount, emailCount: studentHasEmailCount } = counts[
    STUDENT
  ];
  const { total: familyCount, emailCount: familyHasEmailCount } = counts[
    PARENT
  ];

  const totalCount =
    (_.includes(selectedUserGroups, SHARE_WITH_RECIPIENT_TYPE.STAFF)
      ? staffCount
      : 0) +
    (_.includes(selectedUserGroups, SHARE_WITH_RECIPIENT_TYPE.STUDENT)
      ? studentCount
      : 0) +
    (_.includes(selectedUserGroups, SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER)
      ? familyCount
      : 0);

  const totalHasEmailCount =
    (_.includes(selectedUserGroups, SHARE_WITH_RECIPIENT_TYPE.STAFF)
      ? staffHasEmailCount
      : 0) +
    (_.includes(selectedUserGroups, SHARE_WITH_RECIPIENT_TYPE.STUDENT)
      ? studentHasEmailCount
      : 0) +
    (_.includes(selectedUserGroups, SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER)
      ? familyHasEmailCount
      : 0);

  const totalHasEmailCountSelected =
    (_.includes(selectedEmailUserGroups, SHARE_WITH_RECIPIENT_TYPE.STAFF)
      ? staffHasEmailCount
      : 0) +
    (_.includes(selectedEmailUserGroups, SHARE_WITH_RECIPIENT_TYPE.STUDENT)
      ? studentHasEmailCount
      : 0) +
    (_.includes(
      selectedEmailUserGroups,
      SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER
    )
      ? familyHasEmailCount
      : 0);

  return {
    staffCount,
    studentCount,
    familyCount,
    totalCount,
    ...(includeEmailCount
      ? {
          staffHasEmailCount,
          studentHasEmailCount,
          familyHasEmailCount,
          totalHasEmailCount,
          totalHasEmailCountSelected,
        }
      : {}),
  };
};

export const mapIds = entityList => _.map(entityList, "id");

export const getEntities = ({
  entityType,
  programmes,
  grades,
  courses,
  users,
  getOnlyIds = false,
}) => {
  switch (entityType) {
    case ENTITY_TYPE.CURRICULUM_PROGRAM:
      return getOnlyIds ? mapIds(programmes) : programmes;
    case ENTITY_TYPE.GRADE:
      return getOnlyIds ? mapIds(grades) : grades;
    case ENTITY_TYPE.COURSE:
      return getOnlyIds ? mapIds(courses) : courses;
    case ENTITY_TYPE.USER:
      return getOnlyIds ? mapIds(users) : users;
    default:
      return [];
  }
};

export const GROUP_BY_OPTIONS = {
  MEMBERS: "MEMBERS",
  COURSE: "COURSE",
  GRADE: "GRADE",
  YEAR_GROUP: "YEAR_GROUP",
};

export const FILTER_OPTIONS_FOR_CUSTOM_RECIPIENT_KEYS = {
  GROUP_BY: "GROUP_BY",
  STUDENT_CUSTOM_FIELD: "STUDENT_CUSTOM_FIELD",
};

export const getGroupByOptionsForCustomRecipient = ({
  t,
  isInClassAnnouncement,
}) => {
  const baseOptions = [
    {
      key: GROUP_BY_OPTIONS.MEMBERS,
      value: GROUP_BY_OPTIONS.MEMBERS,
      label: t("common:member_other"),
    },
    {
      key: GROUP_BY_OPTIONS.COURSE,
      value: GROUP_BY_OPTIONS.COURSE,
      label: t("common:class_other"),
    },
  ];

  const canPublishProgramOrAll =
    getCanPublishAnnouncementToProgram() || getCanPublishAnnouncementToAll();

  const extraOptions =
    canPublishProgramOrAll && !isInClassAnnouncement
      ? [
          {
            key: GROUP_BY_OPTIONS.GRADE,
            value: GROUP_BY_OPTIONS.GRADE,
            label: t("common:grades"),
          },
          {
            key: GROUP_BY_OPTIONS.YEAR_GROUP,
            value: GROUP_BY_OPTIONS.YEAR_GROUP,
            label: _.startCase(t("common:year_group_plural")),
          },
        ]
      : [];

  return [...baseOptions, ...extraOptions].map(opt => ({
    ...opt,
    parent: FILTER_OPTIONS_FOR_CUSTOM_RECIPIENT_KEYS.GROUP_BY,
  }));
};

export const getStudentCustomFieldDropdownOptions = (node, t) => {
  const globalConstants = _.get(node, "globalConstants", []);

  const studentProfileCustomField = _.get(_.first(globalConstants), "value");
  let customFieldValue = null;

  if (!_.isNil(studentProfileCustomField)) {
    try {
      customFieldValue = JSON.parse(studentProfileCustomField);
    } catch (e) {}
  }

  const dropdownOptions = [];
  let customFieldKey = null;

  _.forOwn(customFieldValue, (value, key) => {
    const showAnnouncement = _.get(value, "showAsAnnouncementRecipientsFilter");
    if (showAnnouncement) {
      customFieldKey = key;
      dropdownOptions.push(..._.get(value, "dropdownOptions", []));
    }
  });

  return {
    dropdownOptions: _.map(dropdownOptions, option => {
      return {
        key: option.value,
        value: option.value,
        label: option.locale ? t(option.locale) : option.label,
        parent: FILTER_OPTIONS_FOR_CUSTOM_RECIPIENT_KEYS.STUDENT_CUSTOM_FIELD,
      };
    }),
    customFieldKey: STUDENT_ADDITIONAL_FIELDS_TITLE_MAPPING[customFieldKey],
    customFieldNo: customFieldKey,
  };
};

export const getFilterOptionsForCustomRecipient = ({
  isInClassAnnouncement = false,
  node,
  t,
}) => {
  const {
    dropdownOptions,
    customFieldKey,
    customFieldNo,
  } = getStudentCustomFieldDropdownOptions(node, t);
  if (!getHasStudentAddFilterEnabled() && _.isEmpty(dropdownOptions)) {
    return {
      filterOptions: getGroupByOptionsForCustomRecipient({
        t,
        isInClassAnnouncement,
      }),
    };
  }

  const studentAdditionalFieldsData = getStudentAdditionalFieldsData();

  const { label = "integration:house_student_details" } =
    _.find(studentAdditionalFieldsData, { fieldNo: customFieldNo }) || {};

  const filterOptions = [
    {
      key: FILTER_OPTIONS_FOR_CUSTOM_RECIPIENT_KEYS.GROUP_BY,
      label: t("common:group_by"),
      options: getGroupByOptionsForCustomRecipient({
        t,
        isInClassAnnouncement,
      }),
      selectionType: "radio",
    },
    {
      key: FILTER_OPTIONS_FOR_CUSTOM_RECIPIENT_KEYS.STUDENT_CUSTOM_FIELD,
      label: t(label),
      options: dropdownOptions,
      selectionType: "checkbox",
    },
  ];

  return {
    filterOptions,
    customFieldKey,
  };
};

export const getCurrentDateMomentObj = () => moment();
export const getCurrentDateTime = () => moment().toISOString();
export const getMomentObj = date => {
  return moment(date);
};
export const combineDateAndTime = ({ time, date }) => {
  const dateAndTime = moment(date);
  dateAndTime.hours(time.hours()).minutes(time.minutes());
  return dateAndTime.toISOString();
};
export const getIsPastTime = ({ date }) => {
  return moment(date).isBefore(moment());
};

export const CHECKBOX_STATE = {
  INDETERMINATE: "INDETERMINATE",
  DETERMINATE: "DETERMINATE",
  EMPTY: "EMPTY",
};

// NOTE: for now not ordering the options as per the group label, due to data issue
export const getClassList = ({
  filteredClasses,
  programFilter,
  groupByCurriculumAndGrade,
  sortByGradeOrder = false,
}) => {
  if (groupByCurriculumAndGrade) {
    return getClassListGroupedByCurriculumAndGrade(filteredClasses);
  }

  // When sortByGradeOrder is enabled and gradeOrder is provided,
  // use a Map pre-populated with the grade order to maintain sequence
  const { grades: gradesOrder } = getOrganizationGradesFromCache({
    variables: {
      organizationId: getOrganizationId(),
      gradeFilters: {
        curriculumProgramIds: getCurriculumProgramIds(),
      },
      getCurriculumProgram: true,
    },
  });

  const useOrderedMap = sortByGradeOrder && !_.isEmpty(gradesOrder);

  let gradeMap = null;
  if (useOrderedMap) {
    gradeMap = new Map();
    _.forEach(gradesOrder, grade => {
      const { name, id } = grade;
      gradeMap.set(id, { label: name, groupId: id, options: [] });
    });
  }

  const obj = {};

  _.forEach(filteredClasses, classItem => {
    const { grades, curriculumProgram } = classItem;

    if (programFilter) {
      const { acronym, id } = curriculumProgram;
      if (_.isArray(obj[id]?.options)) {
        obj[id].options.push(classItem);
      } else {
        obj[id] = { label: acronym, groupId: id, options: [classItem] };
      }
    } else if (useOrderedMap) {
      _.forEach(grades, item => {
        const { name, id } = item;
        const groupObj = gradeMap.get(id);

        if (_.isArray(_.get(groupObj, "options"))) {
          groupObj.options.push({ ...classItem, groupId: id });
        } else {
          gradeMap.set(id, {
            label: name,
            groupId: id,
            options: [{ ...classItem, groupId: id }],
          });
        }
      });
    } else {
      _.forEach(grades, item => {
        const { name, id } = item;

        if (_.isArray(obj[id]?.options)) {
          obj[id].options.push({ ...classItem, groupId: id });
        } else {
          obj[id] = {
            label: name,
            groupId: id,
            options: [{ ...classItem, groupId: id }],
          };
        }
      });
    }
  });

  if (useOrderedMap) {
    return _.filter(
      [...gradeMap.values()],
      groupObj => !_.isEmpty(groupObj.options)
    );
  }

  return _.values(obj);
};

const getClassListGroupedByCurriculumAndGrade = filteredClasses => {
  const curriculumMap = {};

  _.forEach(filteredClasses, classItem => {
    const { grades, curriculumProgram } = classItem;
    const { id: currId, label: currLabel } = curriculumProgram;

    if (!curriculumMap[currId]) {
      curriculumMap[currId] = {
        label: currLabel,
        groupId: currId,
        classCount: 0,
        type: ENTITY_TYPE.COURSE,
        subGroups: {},
      };
    }

    // Each class maps to exactly one curriculum, so increment per class
    curriculumMap[currId].classCount += 1;

    const gradeMap = curriculumMap[currId].subGroups;

    _.forEach(grades, grade => {
      const { name: gradeName, id: gradeId } = grade;
      const compositeId = `${currId}_${gradeId}`;

      if (_.isArray(gradeMap[compositeId]?.options)) {
        gradeMap[compositeId].options.push({
          ...classItem,
          groupId: compositeId,
        });
      } else {
        gradeMap[compositeId] = {
          label: gradeName,
          groupId: compositeId,
          options: [{ ...classItem, groupId: compositeId }],
        };
      }
    });
  });

  return _.reverse(
    _.map(_.values(curriculumMap), curriculum => ({
      ...curriculum,
      subGroups: _.values(curriculum.subGroups),
    }))
  );
};

export const getGradeList = ({
  gradeList,
  curriculumPrograms,
  showLabel = false,
}) => {
  // used map to maintain the order of curriculumPrograms
  const map = _.reduce(
    curriculumPrograms,
    (result, program) => {
      const { id, acronym, label } = program;
      result.set(id, {
        label: showLabel ? label : acronym,
        groupId: id,
        type: ENTITY_TYPE.GRADE,
        options: [],
      });
      return result;
    },
    new Map()
  );

  _.forEach(gradeList, grade => {
    const { curriculumProgram } = grade;
    const { id, acronym, label } = curriculumProgram;
    const groupObj = map.get(id);

    if (_.isArray(_.get(groupObj, "options"))) {
      groupObj.options.push({ ...grade, groupId: id });
    } else {
      // ideally it will never go to else block, but just in case
      map.set(id, {
        label: showLabel ? label : acronym,
        groupId: id,
        options: [{ ...grade, groupId: id }],
      });
    }
  });

  return _.filter([...map.values()], groupObj => !_.isEmpty(groupObj.options));
};

export const TITLE_MAX_LENGTH = 100;

const getNormalizedObject = obj => {
  const res = {};
  // when using _.isEqual, if there are array in object's keys, we need to first sort the array for _.isEqual to work correctly
  _.forEach(obj, (value, key) => {
    if (_.isArray(value)) {
      res[key] = _.sortBy(
        _.map(value, val => {
          return val.id ? val.id : val;
        })
      );
    } else {
      res[key] = value;
    }
  });
  return res;
};

const normalizeEmptyStrings = obj => {
  if (_.isEmpty(obj.title)) obj.title = null;
  if (_.isEmpty(obj.description)) obj.description = null;
  return obj;
};

export const extractIdFromAttachments = attachments => {
  return _.map(attachments, attachment =>
    _.omit(attachment, ATTACHMENT_OMIT_ATTRIBUTES)
  );
};

const KEYS_TO_COMPARE_FOR_ANALYTICS = [
  "state",
  "title",
  "description",
  "attachments",
  "entityType",
  "entities",
  "sendEmail",
  "sharedWith",
];

export const mapEntityTypeToEntity = {
  [ENTITY_TYPE.USER]: "users",
  [ENTITY_TYPE.COURSE]: "courses",
  [ENTITY_TYPE.GRADE]: "grades",
  [ENTITY_TYPE.CURRICULUM_PROGRAM]: "curriculumPrograms",
};

const getEntityDetails = entityMap => {
  const { entityType } = entityMap;
  return {
    entityType,
    entities: _.map(
      _.get(entityMap, mapEntityTypeToEntity[entityType], []),
      "id"
    ),
  };
};

const getAttachmentIdsForComparison = attachments => {
  // If ID is absent, a random ID is assigned for new attachments.
  // When comparing with the server state, it will always be different as expected,
  // since this only occurs for client input.
  return _.map(attachments ?? [], ({ id }, idx) => id || `NEW-${idx}`);
};

const prepareDisplayObjectForAnalytics = ({ input, response }) => ({
  ...input,
  ...response,
  entities: input.entityIds ?? [],
  attachments: getAttachmentIdsForComparison(input.attachments),
});

const prepareServerObjectForAnalytics = ({ serverObject }) => ({
  ...serverObject,
  ...getEntityDetails(serverObject.entityMap),
  attachments: getAttachmentIdsForComparison(serverObject.attachments),
});

const getChangedFields = (displayObject, serverObject) => {
  const comparableDisplayObject = getNormalizedObject(
    _.pick(displayObject, KEYS_TO_COMPARE_FOR_ANALYTICS)
  );
  const comparableServerObject = getNormalizedObject(
    _.pick(serverObject, KEYS_TO_COMPARE_FOR_ANALYTICS)
  );

  const changedFieldsSet = new Set();
  _.forEach(comparableDisplayObject, (val, key) => {
    if (!_.isEqual(val, comparableServerObject[key])) {
      changedFieldsSet.add(key);
    }
  });
  return changedFieldsSet;
};

//this function group courses by grades
export const getCoursesByGrades = ({ courses }) => {
  //1. iterate for each course get itgs grades and push that course to those grades
  const result = _.reduce(
    courses,
    (result, course) => {
      const grades = _.get(course, "grades", []);
      _.forEach(grades, grade => {
        const gradeAdded = result[grade.id];
        if (gradeAdded) {
          const gradeCourses = _.get(gradeAdded, "courses", []);
          gradeCourses.push(course);

          result[grade.id].courses = gradeCourses;
        } else {
          result[grade.id] = { ...grade, courses: [course] };
        }
      });

      return result;
    },
    {}
  );

  return result;
};

export const getCoursesByGradesMemoized = _.memoize(
  params => getCoursesByGrades(params),
  params => JSON.stringify(params)
);

const STATE_TIME_KEY = {
  [ANNOUNCEMENT_STATUSES.DRAFT]: "createdAt",
  [ANNOUNCEMENT_STATUSES.SCHEDULED]: "scheduledFor",
  [ANNOUNCEMENT_STATUSES.PUBLISHED]: "publishedAt",
  [ANNOUNCEMENT_STATUSES.REVIEW_PENDING]: "updatedAt",
  [ANNOUNCEMENT_STATUSES.REVIEW_REJECTED]: "updatedAt",
  [ANNOUNCEMENT_STATUSES.ARCHIVED]: "archivedAt",
};

const ANALYTICS_EVENT_NAME = {
  ANNOUNCEMENT: "Announcement",
  RECIPIENT_CHECK: "Announcement Recipient Checked",
  SEARCH: "Announcement Search",
};

const getAnalyticsAnnouncementObject = announcementObject => {
  const obj = _.pick(announcementObject, KEYS_TO_COMPARE_FOR_ANALYTICS);
  obj.id = announcementObject.id;
  obj.attachments = _.size(obj.attachments);
  obj.entities = _.size(obj.entities);
  obj.isCurriculumAdmin = getCanPublishAnnouncementToProgram();

  // explicitly adding entityType key, so that it should be added as undefined
  // in case of Draft announcement, when no entity is selected
  obj.entityType = announcementObject.entityType;

  if (!_.isUndefined(announcementObject.sendNotification)) {
    obj.sendNotification = announcementObject.sendNotification;
  }

  delete obj.title;
  delete obj.description;
  delete obj.publishedAt;

  obj[STATE_TIME_KEY[obj.state]] = announcementObject.publishedAt;
  return obj;
};

export const recordSaveAnalytics = ({
  userId,
  displayObjectInput,
  displayObjectResponse,
  serverObject, // only would be provided in case of edit announcement
}) => {
  // Wrapped in try/catch to avoid any error in analytics to break the flow
  try {
    const displayObject = prepareDisplayObjectForAnalytics({
      input: displayObjectInput,
      response: displayObjectResponse,
    });
    const prevServerObject = serverObject
      ? prepareServerObjectForAnalytics({ serverObject })
      : undefined;
    const isEdit = !!prevServerObject;
    const analyticsPayload = getAnalyticsAnnouncementObject(displayObject);
    analyticsPayload.isEdit = false;

    if (isEdit) {
      const changedFieldsSet = getChangedFields(
        displayObject,
        prevServerObject
      );
      if (_.isEmpty(changedFieldsSet)) return;

      analyticsPayload.isEdit = true;

      analyticsPayload.isEditorCreator = isIdEqual(
        userId,
        _.get(prevServerObject, "createdBy.id")
      );

      analyticsPayload.didStateChange = changedFieldsSet.has("state");
      analyticsPayload.didTitleChange = changedFieldsSet.has("title");
      analyticsPayload.didDescriptionChange = changedFieldsSet.has(
        "description"
      );
      analyticsPayload.didAttachmentsChange = changedFieldsSet.has(
        "attachments"
      );
      analyticsPayload.didSendEmailChange = changedFieldsSet.has("sendEmail");
      analyticsPayload.didSharedWithChange = changedFieldsSet.has("sharedWith");
      analyticsPayload.didEntityChange =
        changedFieldsSet.has("entities") || changedFieldsSet.has("entityType");
    }

    recordEvent({
      eventName: ANALYTICS_EVENT_NAME.ANNOUNCEMENT,
      eventData: analyticsPayload,
    });
  } catch (err) {}
};

export const recordRecipientsLookupAnalytics = ({ isMyAnnouncement }) => {
  const isCurriculumAdmin = getCanPublishAnnouncementToProgram();
  const analyticPayload = { isMyAnnouncement, isCurriculumAdmin };

  recordEvent({
    eventName: ANALYTICS_EVENT_NAME.RECIPIENT_CHECK,
    eventData: analyticPayload,
  });
};

const CREATED_BY_ANALYTICS_VALUE = {
  ALL: "All",
  SELF: "Self",
  OTHERS: "Others",
};

const mapCreatedByToAnalyticsValue = {
  [FEED_TAB_VALUES.createdByMe]: CREATED_BY_ANALYTICS_VALUE.SELF,
  [FEED_TAB_VALUES.createdByOthers]: CREATED_BY_ANALYTICS_VALUE.OTHERS,
  [FEED_TAB_VALUES.createdByAll]: CREATED_BY_ANALYTICS_VALUE.ALL,
};

export const recordSearchLaunchAnalytics = ({ searchFilters, userId } = {}) => {
  if (!searchFilters?.searchText) {
    recordEvent({ eventName: ANALYTICS_EVENT_NAME.SEARCH });
    return;
  }

  const { searchText, status, createdBy, createdByIds } = searchFilters;
  const createdById = _.get(createdByIds, "[0]");

  const publishedBy = createdById
    ? isIdEqual(userId, createdById)
      ? CREATED_BY_ANALYTICS_VALUE.SELF
      : CREATED_BY_ANALYTICS_VALUE.OTHERS
    : mapCreatedByToAnalyticsValue[createdBy];

  const isCurriculumAdmin = getCanPublishAnnouncementToProgram();

  const analyticPayload = {
    searchText,
    searchScreenFilters: { publishedBy, status },
    isCurriculumAdmin,
  };

  recordEvent({
    eventName: ANALYTICS_EVENT_NAME.SEARCH,
    eventData: analyticPayload,
  });
};

const KEYS_TO_COMPARE_FOR_DISCARD_ALERT = [
  "title",
  "description",
  "attachments",
  "audioMessage",
  "programmes",
  "courses",
  "grades",
  "users",
  "sharedWith",
  "sendEmail",
];

const KEYS_TO_COMPARE_FOR_DISCARD_ALERT_V3 = [
  "title",
  "description",
  "attachments",
  "programmes",
  "courses",
  "grades",
  "users",
  "sharedWith",
  "emailSharedWith",
  "state",
];

const getIsUsingDefaultBanner = displayObject => {
  if (!getIsDefaultBannerEnabled()) {
    return false;
  }
  const isAnnouncementsV3 = getIsAnnouncementsV3Enabled();
  if (!isAnnouncementsV3) {
    return false;
  }
  const { bannerV2 } = displayObject;
  const state = store.getState();
  const organizationId = getOrganizationId(state);
  const academicYearId = getSelectedAcademicYearId(state);
  const currentCurriculumProgramId = getSelectedCurriculumId(state);

  const variables = {
    organizationId,
    filter: {
      names: [CIRCULAR_ATTACHMENT_TYPES.DEFAULT_CIRCULAR_BANNER],
      curriculumProgramIds: _.isNil(currentCurriculumProgramId)
        ? undefined
        : [currentCurriculumProgramId],
      academicYearIds: [academicYearId],
    },
  };
  const defaultBanners = getDefaultBannerFromCache({ variables });
  if (bannerV2) {
    return _.some(
      defaultBanners,
      defaultBanner => defaultBanner.url === bannerV2.url
    );
  }
  return false;
};

export const shouldShowDiscardAlert = ({
  displayObject,
  serverObject,
  isUpdateAnnouncementRoute = false,
  isFromAdminPage = false,
}) => {
  const isAnnouncementsV3 = getIsAnnouncementsV3Enabled();
  const bannerV2 = _.get(displayObject, "bannerV2", null);

  const baseKeysToCompare = isAnnouncementsV3
    ? KEYS_TO_COMPARE_FOR_DISCARD_ALERT_V3
    : KEYS_TO_COMPARE_FOR_DISCARD_ALERT;

  const {
    entityType,
    programmes,
    grades,
    courses,
    users,
    state,
  } = displayObject;

  const isEntitiesSelected = !_.isEmpty(
    getEntities({ entityType, programmes, grades, courses, users })
  );

  let keysToCompare;
  if (isUpdateAnnouncementRoute && state !== ANNOUNCEMENT_STATUSES.DRAFT) {
    keysToCompare = [...baseKeysToCompare, "entityType"];
  } else if (isEntitiesSelected) {
    keysToCompare = [...baseKeysToCompare, "entityType"];
  } else {
    keysToCompare = baseKeysToCompare;
  }

  const defaultSkipEmptyProps = {
    sendEmail: false,
  };

  const keysForEmptyCheck = _.filter(
    keysToCompare,
    key => !_.has(defaultSkipEmptyProps, key)
  );

  const comparableDisplayObject = normalizeEmptyStrings(
    getNormalizedObject(_.pick(displayObject, keysToCompare))
  );
  const comparableServerObject = getNormalizedObject(
    _.pick(serverObject, keysToCompare)
  );

  const isDisplayObjectEmpty = _.every(keysForEmptyCheck, key =>
    _.isEmpty(comparableDisplayObject[key])
  );
  const isDefaultsChangedForSkipEmptyProps = _.every(
    defaultSkipEmptyProps,
    (value, key) => !_.isEqual(value, comparableDisplayObject[key])
  );

  const isServerObjectEmpty = _.isEmpty(comparableServerObject);

  // CreateAnnouncement case
  if (isServerObjectEmpty) {
    const showDiscardAlert =
      !isDisplayObjectEmpty || isDefaultsChangedForSkipEmptyProps;
    return showDiscardAlert;
  }
  // UpdateAnnouncement case
  if (isAnnouncementsV3) {
    const isUsingDefaultBanner = getIsUsingDefaultBanner(displayObject);
    const isDraft = displayObject.state === ANNOUNCEMENT_STATUSES.DRAFT;
    const isPublishedUpdate = isUpdateAnnouncementRoute && !isDraft;
    const isSameContent = _.isEqual(
      comparableDisplayObject,
      comparableServerObject
    );
    const isSameBanner = bannerV2?.url === serverObject?.bannerV2?.url;

    if (!_.isNil(bannerV2)) {
      // no need to check for isUsingDefaultBanner for published and admin announcements
      if (isFromAdminPage || isPublishedUpdate) {
        return !(isSameContent && isSameBanner);
      }

      return !(isSameContent && (isUsingDefaultBanner || isSameBanner));
    }

    if (isPublishedUpdate && !_.isNil(serverObject.bannerV2)) {
      return true;
    }
  }

  return !_.isEqual(comparableDisplayObject, comparableServerObject);
};

export const buildLearningCourseClassesFilter = ({
  learningCourseId,
  curriculumProgramId,
  academicYearId,
  searchText,
}) => ({
  fetchOnlyMappedCourses: false,
  controlParameters: [CLASS_ANNOUNCEMENT_MANAGE_PERMISSION],
  learningCourseIds: [learningCourseId],
  curriculumProgramIds: [curriculumProgramId],
  ...(academicYearId && { academicYears: [academicYearId] }),
  ...(searchText && { searchText }),
});

export const getClassCourseIds = ({
  learningCourseId,
  selectedCourseId,
  entityType,
}) => {
  if (entityType !== ENTITY_TYPE.USER || _.isEmpty(selectedCourseId)) return [];

  if (_.isEmpty(learningCourseId)) {
    return [selectedCourseId];
  }

  const state = store.getState();
  const userId = getUserId(state);
  const academicYearId = getSelectedAcademicYearId(state);
  const currentCurriculumId = getSelectedCurriculumId(state);

  const learningCourseClasses = getUserLearningCourseClassesFromCache({
    variables: {
      id: userId,
      userType: "STAFF",
      filters: buildLearningCourseClassesFilter({
        learningCourseId,
        curriculumProgramId: currentCurriculumId,
        academicYearId,
      }),
    },
  });

  return _.map(learningCourseClasses, "id");
};

export const getCourseIdsForUsersPool = ({
  selectedCourseId,
  learningCourseId,
}) => {
  const state = store.getState();

  const userId = getUserId(state);
  const academicYearId = getSelectedAcademicYearId(state);

  if (!_.isEmpty(selectedCourseId) && !_.isEmpty(learningCourseId)) {
    const currentCurriculumId = getSelectedCurriculumId(state);
    const courses = getUserLearningCourseClassesFromCache({
      variables: {
        id: userId,
        userType: "STAFF",
        filters: buildLearningCourseClassesFilter({
          learningCourseId,
          curriculumProgramId: currentCurriculumId,
          academicYearId,
        }),
      },
    });
    return _.map(courses, "id");
  } else if (!_.isEmpty(selectedCourseId)) {
    return [selectedCourseId];
  }
  return undefined;
};

export const ANNOUNCEMENT_COMPOSE_SIDEBAR_CONTENT = {
  PREVIEW: "preview",
  ASSISTANT: "assistant",
  SIDEBAR_CLOSE: "sidebar_close",
};

export const ATTACHMENT_ACCEPT_TYPES = [
  // Currently not using * as it is not supported by our OneDrive/GoogleDrive Component
  // "image/*",
  // "video/*",
  // "audio/*",

  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/bmp",
  "image/tiff",
  "image/svg",
  "image/svg+xml",
  "image/webp",
  "image/heic",
  "image/heif",

  "video/mp4",
  "video/mpeg",
  "video/avi",
  "video/webm",
  "video/quicktime",
  "video/wmv",
  "video/x-matroska",
  "video/webm;codecs=vp8,opus",

  "audio/mpeg",
  "audio/x-m4a",
  "audio/mp4",
  "audio/mp3",
  "audio/wav",
  "audio/wave",
  "audio/vnd.wave",
  "audio/vnd.wav",
  "audio/x-wav",
  "audio/aac",

  "text/plain",
  "application/pdf",

  // support the docs, sheets and slides from Google drive
  "application/vnd.google-apps.document",
  "application/vnd.google-apps.presentation",
  "application/vnd.google-apps.spreadsheet",

  "application/msword", // .doc
  "application/vnd.ms-excel", // .xls
  "application/vnd.ms-powerpoint", // .ppt

  "application/vnd.ms-word.document.macroenabled.12", // .docm
  "application/vnd.ms-excel.sheet.macroenabled.12", // .xlsm
  "application/vnd.ms-powerpoint.presentation.macroenabled.12", // .pptm

  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx

  // Currently not adding the support for files with "odt", "ods" and "odp" extension
  // As their usage is less and its preview is not supported by our FileViewer component
  // "application/vnd.oasis.opendocument.text", // odt
  // "application/vnd.oasis.opendocument.spreadsheet", // ods
  // "application/vnd.oasis.opendocument.presentation", // odp
];

export const trimLeadingAndConsecutiveWhiteSpace = inputString => {
  if (!inputString) return "";
  // Replace multiple consecutive whitespace and &nbsp; with a single whitespace
  const stringWithSingleWhitespace = inputString.replace(/[\s\u00a0]+/g, " ");

  return stringWithSingleWhitespace.trim();
};

export const getUnreadAnnouncementsCountInput = state => {
  const curriculumProgramId = getSelectedCurriculumId(state);
  const academicYearId = getSelectedAcademicYearId(state);
  const studentIds = getChildId(state);
  const curriculumProgramIds = curriculumProgramId ? [curriculumProgramId] : [];
  return {
    createdBy: FEED_TAB_VALUES.createdByAll,
    states: [ANNOUNCEMENT_STATUSES.PUBLISHED],
    curriculumProgramIds,
    academicYearId,
    isRead: false,
    createdIn: CREATED_IN_OPTIONS.CLASS,
    ...(!_.isNil(studentIds) ? { studentIds } : {}),
  };
};

export const getEntityWithCountText = ({ t, entityType, entities = [] }) => {
  const isAccountConfigurationEnabled = getIsAccountConfigurationEnabled();

  //Change entity type curriculum program to school for 2.0 orgs
  const entity =
    entityType === ENTITY_TYPE.CURRICULUM_PROGRAM &&
    isAccountConfigurationEnabled
      ? "school"
      : entityType;

  return t(`announcement:${_.toLower(entity)}_selected_with_count`, {
    count: _.size(entities),
  });
};

export const getCoursesCohorts = ({
  edges,
  skipStaff,
  skipStudents,
  skipFamilyMembers,
}) => {
  const coursesCohorts = [];
  _.forEach(edges, course => {
    const {
      students = {},
      collaborator = {},
      familyMembers = {},
      id,
      title,
      grades = [],
      curriculumProgram = {},
    } = _.get(course, "node", course);

    if (!skipStaff) {
      const collaboratorList = _.map(_.get(collaborator, "edges", []), "node");
      coursesCohorts.push({
        id,
        title,
        type: STAFF,
        userList: collaboratorList,
        grades,
        curriculumProgram,
      });
    }
    if (!skipStudents) {
      const studentsList = _.map(_.get(students, "edges", []), "node");

      coursesCohorts.push({
        id,
        title,
        type: STUDENT,
        userList: studentsList,
        grades,
        curriculumProgram,
      });
    }
    if (!skipFamilyMembers) {
      const familyMembersList = _.map(
        _.get(familyMembers, "edges", []),
        "node"
      );
      coursesCohorts.push({
        id,
        title,
        type: PARENT,
        userList: familyMembersList,
        grades,
        curriculumProgram,
      });
    }
  });
  return coursesCohorts;
};

export const getGradesCohorts = ({
  edges,
  skipStaff,
  skipStudents,
  skipFamilyMembers,
}) => {
  const gradeCohorts = [];
  _.forEach(edges, gradeItem => {
    const grade = _.get(gradeItem, "node", gradeItem);
    const {
      allStudents = [],
      allStaffs = [],
      allParents = [],
      id,
      name: title,
      curriculumProgram = {},
    } = grade;

    if (!skipStaff) {
      gradeCohorts.push({
        id,
        title,
        type: STAFF,
        userList: allStaffs,
        curriculumProgram,
      });
    }
    if (!skipStudents) {
      gradeCohorts.push({
        id,
        title,
        type: STUDENT,
        userList: allStudents,
        curriculumProgram,
      });
    }
    if (!skipFamilyMembers) {
      gradeCohorts.push({
        id,
        title,
        type: PARENT,
        userList: allParents,
        curriculumProgram,
      });
    }
  });
  return gradeCohorts;
};
